import Badge from "@/components/core/badge";
import { ScrollArea } from "@/components/core/scroller";
import { Searchbar } from "@/components/core/searchbar";
import { Content } from "@/components/layouts/content";
import { Header } from "@/components/layouts/header";
import { helpCategoriesQueryOptions } from "@/features/dashboard/api/queries/help-category-queries";
import { helpCenterQueryOptions } from "@/features/dashboard/api/queries/help-center-queries";
import type { HelpTopic } from "@/features/dashboard/types/help-topic";
import { IconArrowRight, IconChevronDown, IconChevronUp, IconNotes } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";

type Topic = {
	id?: string;
	title: string;
	description?: string;
	body?: string;
	attachment?: {
		original_url: string;
		mime_type?: string;
	};
};

export const Route = createFileRoute("/help-center")({
	component: Page,
	loader: ({ context: { queryClient }, deps }) => {
		return queryClient.ensureQueryData(
			helpCategoriesQueryOptions({
				...deps,
			}),
		);
	},
	loaderDeps: ({ search }) => ({
		search: search,
	}),
});

function Page() {
	const [openCategories, setOpenCategories] = useState<{
		[key: string]: boolean;
	}>({});
	const [selectedTopic, setSelectedTopic] = useState<HelpTopic | null>(null);

	const toggleCategory = (slug: string) => {
		setOpenCategories((prev) => ({
			...prev,
			[slug]: !prev[slug],
		}));
		console.log(openCategories[slug]);
	};

	const handleTopicClick = (topic: HelpTopic) => {
		setSelectedTopic(topic);
		setSearch("");
		setOpenCategories((prev) => ({
			...prev,
			[topic.category?.slug || ""]: true,
		}));
	};

	const navigate = useNavigate();
	const [search, setSearch] = useState("");
	const helpCategoriesQuery = useSuspenseQuery(
		helpCategoriesQueryOptions({
			include: "topics",
		}),
	);
	const searchResultsQuery = useSuspenseQuery(
		helpCenterQueryOptions({
			search: search,
		}),
	);
	const searchResults = searchResultsQuery.data.data;

	const categories = helpCategoriesQuery.data.data;

	const onClear = async () => {
		setSearch("");
		await navigate({
			to: "/help-center",
			search: "",
			replace: true,
		});
	};

	const regex = /(<([^>]+)>)/gi;
	const sanitizeText = (text: string) => {
		return text.replace(regex, "");
	};

	const TopicSearch = () => {
		return (
			<div className="flex flex-col w-full max-w-[720px] mx-auto gap-4">
				<div className="flex flex-col w-full gap-2">
					<div className="text-center flex flex-col w-full">
						<div className="text-center text-xl font-semibold">Help Center</div>
					</div>
					{searchResults && (
						<div className="flex flex-row w-full max-w-[720px]">
							<Searchbar
								value={search}
								onChange={async (value) => {
									setSearch(value);
									await navigate({
										to: "/help-center",
										search: (old: any) => ({
											...old,
										}),
										replace: true,
									});
								}}
								onClear={onClear}
							/>
						</div>
					)}
				</div>
				<div className="flex flex-col w-full">
					{searchResults.map((result) => (
						<button
							key={result.id}
							type="button"
							className="group flex flex-row p-3 gap-3 rounded-md cursor-pointer hover:bg-slate-50"
							onClick={() => handleTopicClick(result)}
						>
							<div className="inline-flex shrink-0 justify-center items-center w-[44px] h-[44px] rounded-full bg-primary-600 text-primary-foreground">
								<IconNotes className="inline-flex group-hover:hidden" stroke={1.5} size={24} />
								<IconArrowRight className="hidden group-hover:inline-flex" stroke={1.5} size={24} />
							</div>
							<div className="flex flex-col gap-1">
								<Badge className="self-start uppercase" size="xs" color="default-light">
									{result.category?.label}
								</Badge>
								<div className="flex flex-col w-full">
									<span className="text-left text-base font-semibold text-foreground-dark">{result.title}</span>
									<span className="text-left text-base text-foreground line-clamp-2">
										{result.description ? result.description : sanitizeText(result.body)}
									</span>
								</div>
							</div>
						</button>
					))}
				</div>
			</div>
		);
	};

	const TopicDisplay = ({ selectedTopic }: { selectedTopic: Topic }) => {
		const videoUrl = selectedTopic.attachment?.original_url;

		return (
			<div className="flex flex-col w-full max-w-[720px] mx-auto">
				<div className="font-bold text-xl mb-2">{selectedTopic.title}</div>
				<div className="text-base text-foreground mb-4">{selectedTopic.description}</div>
				{selectedTopic?.attachment?.original_url ? (
					<div className="text-base text-foreground mb-4">
						<video className="block w-full" disablePictureInPicture playsInline controls>
							<source src={videoUrl} />
						</video>
					</div>
				) : null}
				<div
					className="text-base text-foreground prose editor"
					// biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
					dangerouslySetInnerHTML={{
						__html: selectedTopic.body || "",
					}}
					key={Date()}
				/>
			</div>
		);
	};

	return (
		<Content pageName="Help Center">
			{/*<Header title="Help Center" />*/}
			<div className="flex flex-row w-full pl-[380px]">
				<div className="fixed top-[90px] left-0 bottom-0 flex flex-col w-full max-w-[380px] border-r border-border/50">
					<div className="flex flex-col w-full h-full">
						{selectedTopic && !search ? (
							<div className="flex flex-row w-full px-6 pt-6 pb-3">
								<Searchbar
									value={search}
									onChange={async (value) => {
										setSearch(value);
										await navigate({
											to: "/help-center",
											search: (old: any) => ({
												...old,
											}),
											replace: true,
										});
									}}
									onClear={onClear}
								/>
							</div>
						) : null}

						<ScrollArea className="h-[calc(100svh-180px)]">
							<div className="flex flex-col w-full px-6 pt-3 pb-6 overflow-y-auto">
								{categories.map((category) => (
									<div key={category.slug} className="flex flex-col w-full">
										<button
											type="button"
											onClick={() => toggleCategory(category.slug || "")}
											className="flex flex-row w-full items-center justify-between text-left py-1 text-base font-semibold text-foreground-dark hover:text-primary-600"
										>
											<span className="">{category.name}</span>
											{openCategories[category.slug || ""] ? (
												<IconChevronUp className="text-foreground/50" size={18} stroke={2} />
											) : (
												<IconChevronDown className="text-foreground/50" size={18} stroke={2} />
											)}
										</button>

										{openCategories[category.slug || ""] && (
											<div className="flex flex-col w-full pl-1">
												<div className="flex flex-col w-full pl-3 mb-2 border-l border-primary-600">
													{category.topics?.map((topic) => (
														<button
															key={topic.id}
															type="button"
															onClick={() => handleTopicClick(topic)}
															className="py-1 text-base text-foreground/70 hover:text-primary-600 text-left"
														>
															{topic.title}
														</button>
													))}
												</div>
											</div>
										)}
									</div>
								))}
							</div>
						</ScrollArea>
					</div>
				</div>

				<div className="flex flex-col items-center w-full h-[calc(100svh-90px)] overflow-hidden">
					<div className="flex flex-col w-full h-full">
						<ScrollArea className="h-[calc(100svh-90px)] p-6">
							{selectedTopic && !search ? <TopicDisplay selectedTopic={selectedTopic} /> : searchResults && <TopicSearch />}
							{/* {searchResults && <TopicSearch />} */}
						</ScrollArea>
					</div>
				</div>
			</div>
		</Content>
	);
}
